import React from 'react';
import { Card } from 'antd';
import { SkBar } from './_primitives';

const OrdersHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div className="w-12 h-12 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex flex-col gap-2 min-w-0">
        <SkBar h="h-7" w="w-56" className="max-w-full" />
        <SkBar h="h-3" w="w-48" className="max-w-full" />
      </div>
    </div>
    <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
  </div>
);

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex flex-col gap-3">
      <SkBar h="h-3" w="w-32" />
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-gray-200 animate-pulse flex-shrink-0" />
        <SkBar h="h-6" w="w-16" />
      </div>
    </div>
  </Card>
);

const SearchBarCard = () => (
  <Card className="shadow-sm mb-4" styles={{ body: { padding: 16 } }}>
    <div className="flex flex-col sm:flex-row gap-3 items-stretch">
      <SkBar h="h-10" w="w-full sm:flex-1" className="rounded-md" />
      <SkBar h="h-10" w="w-full sm:w-48" className="rounded-md flex-shrink-0" />
      <SkBar h="h-10" w="w-full sm:w-32" className="rounded-md flex-shrink-0" />
    </div>
  </Card>
);

const TabsCard = () => (
  <Card className="shadow-sm mb-4" styles={{ body: { padding: 20 } }}>
    <div className="flex gap-6 sm:gap-8 border-b border-gray-200 -mb-5 overflow-x-auto">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className={`pb-3 flex items-center gap-2 flex-shrink-0 ${
            i === 0 ? 'border-b-2 border-gray-300' : ''
          }`}
        >
          <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
          <SkBar h="h-4" w="w-28" />
        </div>
      ))}
    </div>
  </Card>
);

const ORDER_COLS = ['w-14', 'w-20', 'w-16', 'w-20', 'w-20', 'w-20', 'w-16', 'w-10', 'w-14'];

const OrderRow = () => (
  <div className="grid grid-cols-[0.6fr_1.5fr_1.2fr_1fr_1.1fr_1.1fr_1fr_0.5fr_0.8fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Order # */}
    <SkBar h="h-3" w="w-10" />
    {/* Customer: name + phone */}
    <div className="flex flex-col gap-1.5 min-w-0">
      <SkBar h="h-3" w="w-28" />
      <SkBar h="h-2" w="w-24" />
    </div>
    {/* Location */}
    <SkBar h="h-3" w="w-20" />
    {/* Order Type pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md" />
    {/* Order Status pill */}
    <SkBar h="h-6" w="w-20" className="rounded-md" />
    {/* Order Source pill */}
    <SkBar h="h-6" w="w-20" className="rounded-md" />
    {/* Placed By pill */}
    <SkBar h="h-6" w="w-20" className="rounded-md" />
    {/* Items */}
    <SkBar h="h-3" w="w-4" />
    {/* View action */}
    <div className="flex items-center gap-1 justify-end">
      <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
      <SkBar h="h-3" w="w-8" />
    </div>
  </div>
);

const OrdersPagination = () => (
  <div className="flex items-center justify-end gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-24" />
    <div className="flex items-center gap-2">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
    <SkBar h="h-8" w="w-24" className="rounded-md" />
  </div>
);

/**
 * OrdersPageLoader — matches Order Management layout:
 *  icon header + title/subtitle + Refresh · 4 stat cards (label + icon + value) ·
 *  search bar card (search + sort + filters) · tabs card (3 tabs with icons) ·
 *  orders table card (9 cols) · right-aligned pagination.
 */
const OrdersPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <OrdersHeader />

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    <SearchBarCard />
    <TabsCard />

    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <div className="overflow-x-auto">
        <div className="min-w-[1200px]">
          <div className="grid grid-cols-[0.6fr_1.5fr_1.2fr_1fr_1.1fr_1.1fr_1fr_0.5fr_0.8fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
            {ORDER_COLS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === ORDER_COLS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 10 }).map((_, i) => (
            <OrderRow key={i} />
          ))}
        </div>
      </div>
      <OrdersPagination />
    </Card>
  </div>
);

export default OrdersPageLoader;
